import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { HeaderPro } from '../../components/HeaderPro';
import { Footer } from '../../components/Footer';
import { PrimaryButton } from '../../components/PrimaryButton';
import { confirmAllAttendance } from '../../../lib/pro-attendance-api';
import { apiErrorMessage } from '../../../lib/api';

/**
 * One-click "Confirm all" landing fired from the Daily Schedule digest
 * email. POSTs the token immediately on mount, then shows a summary +
 * a link into the full per-booking management page so the pro can
 * still review / cancel if needed.
 */
export default function AttendanceConfirmAllPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('t') ?? '';
  const [state, setState] = useState<
    | { kind: 'loading' }
    | { kind: 'done'; confirmedCount: number }
    | { kind: 'error'; message: string }
  >({ kind: 'loading' });
  const ranRef = useRef(false);

  useEffect(() => {
    // React strict-mode double-mount guard so we never fire two
    // confirm requests for the same click.
    if (ranRef.current) return;
    ranRef.current = true;
    if (!token) {
      setState({
        kind: 'error',
        message:
          'This link is missing its access token. Open it from the email we sent.',
      });
      return;
    }
    (async () => {
      try {
        const res = await confirmAllAttendance(token);
        setState({ kind: 'done', confirmedCount: res.confirmed_count ?? 0 });
      } catch (err) {
        setState({
          kind: 'error',
          message: apiErrorMessage(
            err,
            'We could not confirm your bookings. Open the email and try again.',
          ),
        });
      }
    })();
  }, [token]);

  const manageHref = token
    ? `/pro/attendance?t=${encodeURIComponent(token)}`
    : '/pro/attendance';

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <HeaderPro />
      <main className="flex-1 max-w-[560px] w-full mx-auto px-4 py-16 text-center">
        {state.kind === 'loading' ? (
          <>
            <div className="w-12 h-12 mx-auto rounded-full border-4 border-[#8e2a2f]/20 border-t-[#8e2a2f] animate-spin mb-6" />
            <h1 className="font-['Playfair_Display',sans-serif] font-extrabold text-[#8e2a2f] text-[24px]">
              Confirming your bookings…
            </h1>
          </>
        ) : state.kind === 'done' ? (
          <>
            <h1 className="font-['Playfair_Display',sans-serif] font-extrabold text-[#8e2a2f] text-[28px] md:text-[34px] leading-[40px] md:leading-[48px] mb-3">
              {state.confirmedCount > 0
                ? `Confirmed ${state.confirmedCount} booking${state.confirmedCount === 1 ? '' : 's'}!`
                : 'Already confirmed.'}
            </h1>
            <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[14px] mb-8">
              {state.confirmedCount > 0
                ? 'Thanks — the customers know to expect you.'
                : "Looks like you'd already confirmed these. Nothing to do."}
            </p>
            <Link to={manageHref} className="inline-block">
              <PrimaryButton className="w-auto px-8 !h-[44px]">
                Review individual bookings
              </PrimaryButton>
            </Link>
          </>
        ) : (
          <>
            <h1 className="font-['Playfair_Display',sans-serif] font-extrabold text-[#8e2a2f] text-[28px] md:text-[34px] leading-[40px] md:leading-[48px] mb-3">
              We couldn't confirm
            </h1>
            <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[14px] mb-8">
              {state.message}
            </p>
            <Link to={manageHref} className="inline-block">
              <PrimaryButton className="w-auto px-8 !h-[44px]">
                Open my bookings
              </PrimaryButton>
            </Link>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
